import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Box, FormControlLabel, Grid, Radio, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import PageContainer from '../../components/layout/PageContainer';
import {
  FBoxContainer,
  FHAutocomplete,
  FHButton,
  FHPageLink,
  FHRadioGroup,
  FProgressBar,
} from '../../components/base';
import { providerActions } from '../../store';
import { QaBillingStatus } from '../constants/general';

import type { RootState } from '../../store';
import type { IOption, IProvider } from '../../utils/types';
import type { ChangeEvent } from 'react';

function ChangeQaBillingStatus() {
  const { providerId } = useParams<{ providerId: string }>();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const {
    lastFetchedProvider: provider,
    list: providersList,
    loading: providerLoading,
  } = useSelector((state: RootState) => state.providers);

  const [selectedProvider, setSelectedProvider] = useState<IProvider | IOption | null>(null);
  const [billingStatus, setBillingStatus] = useState<QaBillingStatus | null>(null);

  useEffect(() => {
    dispatch(providerActions.getList({ getAccessTokenSilently }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (providerId && providersList.length > 0) {
      const providerFromList = providersList.find((p) => p.id === Number(providerId));
      if (providerFromList) {
        setSelectedProvider({
          id: providerFromList.id,
          label: `${providerFromList.first_name} ${providerFromList.last_name}`,
        });
      } else {
        setSelectedProvider(null);
        toast.error('Clinical provider not found');
      }
    }
  }, [providerId, providersList]);

  useEffect(() => {
    if (!selectedProvider) {
      setBillingStatus(null);
      return;
    }
    const fetchProvider = async () => {
      await dispatch(
        providerActions.getProviderById({
          providerId: selectedProvider.id,
          getAccessTokenSilently,
        }),
      );
    };
    fetchProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]);

  useEffect(() => {
    if (provider) {
      setBillingStatus(provider.qa_billing_status || null);
    }
  }, [provider]);

  const handleChangeBillingStatus = async () => {
    if (!selectedProvider) return;

    const res = await dispatch(
      providerActions.updateQaBillingStatus({
        providerId: selectedProvider.id,
        data: { qa_billing_status: billingStatus },
        getAccessTokenSilently,
      }),
    );

    if (!res.error) {
      await dispatch(
        providerActions.getProviderById({
          providerId: selectedProvider.id,
          getAccessTokenSilently,
        }),
      );
    }
  };

  return (
    <PageContainer>
      {providerLoading && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography textAlign={'left'} variant="h4">
              Change QA billing status
            </Typography>
          </Box>
          <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }} />
          <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
            <Grid item xs={2.5}>
              <Typography textAlign={'left'} variant="h6">
                Provider:
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <FHAutocomplete
                inputHeight={40}
                sx={{ width: '300px', boxShadow: 'none' }}
                placeholder="Select provider"
                options={providersList.map((provider: IProvider) => ({
                  id: provider.id,
                  label: `${provider.first_name} ${provider.last_name}`,
                }))}
                renderOption={(props: any, option: IOption) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
                value={selectedProvider}
                onChange={(_event: ChangeEvent, newValue: IProvider | null) => {
                  setSelectedProvider(newValue);
                }}
              />
            </Grid>
          </Grid>
          <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'} mt={1}>
            <Grid item xs={2.5}>
              <Typography textAlign={'left'} variant="h6">
                Billing status:
              </Typography>
            </Grid>
            <Grid item xs={8} display={'flex'} alignItems={'center'}>
              <FHRadioGroup
                row
                rowGap={'0'}
                value={billingStatus}
                sx={{
                  justifyContent: 'flex-start',
                  '& .MuiFormControlLabel-root': {
                    marginRight: 4,
                  },
                  marginBottom: 0,
                }}
                name="existing-provider-radio-buttons-group"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setBillingStatus(
                    e.target.value === QaBillingStatus.BILLABLE
                      ? QaBillingStatus.BILLABLE
                      : QaBillingStatus.NOT_BILLABLE,
                  );
                }}
              >
                <FormControlLabel
                  value={QaBillingStatus.BILLABLE}
                  control={<Radio />}
                  label="Billable"
                />
                <FormControlLabel
                  value={QaBillingStatus.NOT_BILLABLE}
                  control={<Radio />}
                  label="Not Billable"
                />
              </FHRadioGroup>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
            <FHButton
              data-testid="resetStatusButton"
              variant="contained"
              sx={{ mt: 2, width: '200px' }}
              onClick={handleChangeBillingStatus}
              disabled={
                providerLoading ||
                !selectedProvider ||
                !billingStatus ||
                billingStatus === provider?.qa_billing_status
              }
            >
              Update
            </FHButton>
          </Box>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default ChangeQaBillingStatus;
